import { useEffect, useMemo, useState } from "react";
import { Plus, AlertOctagon, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import Modal from "@/components/Modal";
import PhotoUpload from "@/components/PhotoUpload";
import EmptyState from "@/components/EmptyState";
import {
    INCIDENT_STATE_LABELS,
    INCIDENT_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

const COLUMN_STATES = ["open", "assigned", "in_progress", "awaiting_material", "resolved", "closed"];

export default function Incidents() {
    const { meta } = useAuth();
    const [incidents, setIncidents] = useState([]);
    const [properties, setProperties] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState({
        property_id: "",
        title: "",
        type: "plumbing",
        priority: "medium",
        description: "",
        photo: null,
        assigned_to: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/incidents").then((r) => setIncidents(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => {
        load();
        api.get("/properties").then((r) => setProperties(r.data || []));
        api.get("/employees").then((r) => setEmployees(r.data || []));
    }, []);

    const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
    const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

    const grouped = useMemo(() => {
        const g = Object.fromEntries(COLUMN_STATES.map((s) => [s, []]));
        for (const inc of incidents) {
            (g[inc.state] || g.open).push(inc);
        }
        return g;
    }, [incidents]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.property_id || !form.title) {
            setError("Propiedad y título son obligatorios");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await api.post("/incidents", { ...form, assigned_to: form.assigned_to || null });
            setOpen(false);
            setForm({
                property_id: "",
                title: "",
                type: "plumbing",
                priority: "medium",
                description: "",
                photo: null,
                assigned_to: "",
            });
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const updateState = async (id, newState) => {
        const { data } = await api.patch(`/incidents/${id}`, { state: newState });
        setIncidents((all) => all.map((inc) => (inc.id === id ? data : inc)));
        if (detail?.id === id) setDetail(data);
    };

    const saveDetail = async (patch) => {
        const { data } = await api.patch(`/incidents/${detail.id}`, patch);
        setIncidents((all) => all.map((inc) => (inc.id === data.id ? data : inc)));
        setDetail(data);
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar incidencia?")) return;
        await api.delete(`/incidents/${id}`);
        setDetail(null);
        load();
    };

    const types = meta?.incident_types || Object.keys(INCIDENT_TYPE_LABELS);
    const priorities = meta?.incident_priorities || Object.keys(PRIORITY_LABELS);
    const states = meta?.incident_states || COLUMN_STATES;

    return (
        <div className="space-y-6" data-testid="incidents-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Centro de incidencias
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">Incidencias</h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Pipeline kanban con prioridad y estado en tiempo real.
                    </p>
                </div>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-incident-button">
                    <Plus size={16} /> Nueva incidencia
                </button>
            </div>

            {loading ? (
                <div className="zentra-skeleton h-96" />
            ) : incidents.length === 0 ? (
                <EmptyState
                    icon={AlertOctagon}
                    title="Sin incidencias"
                    description="Cuando se reporten incidencias, aparecerán aquí en formato kanban."
                />
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="incidents-kanban">
                    {COLUMN_STATES.map((s) => (
                        <div key={s} className="bg-zentra-bg/50 border border-zentra-border rounded-lg p-3 min-h-[300px]" data-testid={`col-${s}`}>
                            <div className="flex items-center justify-between mb-3">
                                <StateBadge value={s} dict={INCIDENT_STATE_LABELS} />
                                <span className="text-xs font-display font-semibold text-zentra-muted">
                                    {grouped[s].length}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {grouped[s].map((inc) => (
                                    <button
                                        key={inc.id}
                                        type="button"
                                        onClick={() => setDetail(inc)}
                                        className="w-full text-left zentra-card p-3 hover:border-zentra-olive group"
                                        data-testid={`incident-card-${inc.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <span className="text-sm font-medium text-zentra-ink line-clamp-2">
                                                {inc.title}
                                            </span>
                                            {inc.priority === "critical" && (
                                                <span className="w-2 h-2 rounded-full bg-red-600 zentra-pulse flex-shrink-0 mt-1" />
                                            )}
                                        </div>
                                        <p className="text-[11px] text-zentra-muted line-clamp-1">
                                            {propMap[inc.property_id]?.name || ""}
                                        </p>
                                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                                            <StateBadge value={inc.priority} dict={PRIORITY_LABELS} />
                                            <span className="text-[10px] text-zentra-muted">
                                                {INCIDENT_TYPE_LABELS[inc.type] || inc.type}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* New incident modal */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nueva incidencia"
                subtitle="Reporta un problema asociado a una propiedad."
                wide
                testId="incident-modal"
                footer={
                    <>
                        <button type="button" className="zentra-btn-ghost" onClick={() => setOpen(false)}>
                            Cancelar
                        </button>
                        <button type="submit" form="incident-form" className="zentra-btn-primary" disabled={saving} data-testid="save-incident">
                            {saving ? "Creando…" : "Crear incidencia"}
                        </button>
                    </>
                }
            >
                <form id="incident-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="zentra-label">Propiedad</label>
                        <select
                            className="zentra-select"
                            required
                            value={form.property_id}
                            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                            data-testid="incident-property"
                        >
                            <option value="">Seleccionar propiedad…</option>
                            {properties.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.code} · {p.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Título</label>
                        <input
                            className="zentra-input"
                            required
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="Caldera no enciende"
                            data-testid="incident-title"
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Tipo</label>
                        <select
                            className="zentra-select"
                            value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                            data-testid="incident-type"
                        >
                            {types.map((t) => (
                                <option key={t} value={t}>
                                    {INCIDENT_TYPE_LABELS[t] || t}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Prioridad</label>
                        <select
                            className="zentra-select"
                            value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: e.target.value })}
                        >
                            {priorities.map((p) => (
                                <option key={p} value={p}>
                                    {PRIORITY_LABELS[p]?.label || p}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Descripción</label>
                        <textarea
                            className="zentra-textarea min-h-[80px]"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Detalles del problema…"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Asignar técnico</label>
                        <select
                            className="zentra-select"
                            value={form.assigned_to}
                            onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                        >
                            <option value="">Sin asignar</option>
                            {employees
                                .filter((e) => e.role === "technician" || e.role === "supervisor")
                                .map((e) => (
                                    <option key={e.id} value={e.id}>
                                        {e.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Evidencia fotográfica</label>
                        <PhotoUpload value={form.photo} onChange={(d) => setForm({ ...form, photo: d })} testId="incident-photo" />
                    </div>
                    {error && (
                        <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
                    )}
                </form>
            </Modal>

            {/* Detail modal */}
            <Modal
                open={!!detail}
                onClose={() => setDetail(null)}
                title={detail?.title}
                subtitle={detail ? `${INCIDENT_TYPE_LABELS[detail.type] || detail.type} · ${propMap[detail.property_id]?.name || ""}` : ""}
                wide
                testId="incident-detail-modal"
                footer={
                    detail && (
                        <>
                            <button className="zentra-btn-danger" onClick={() => remove(detail.id)}>
                                <Trash2 size={14} />
                            </button>
                            <div className="flex-1" />
                            <select
                                className="zentra-select w-auto"
                                value={detail.state}
                                onChange={(e) => updateState(detail.id, e.target.value)}
                                data-testid="incident-state-select"
                            >
                                {states.map((s) => (
                                    <option key={s} value={s}>
                                        {INCIDENT_STATE_LABELS[s]?.label || s}
                                    </option>
                                ))}
                            </select>
                            <button className="zentra-btn-primary" onClick={() => setDetail(null)}>
                                Cerrar
                            </button>
                        </>
                    )
                }
            >
                {detail && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <StateBadge value={detail.priority} dict={PRIORITY_LABELS} />
                            <StateBadge value={detail.state} dict={INCIDENT_STATE_LABELS} />
                            {empMap[detail.assigned_to] && (
                                <span className="zentra-chip bg-zentra-primary text-white border-zentra-primary">
                                    {empMap[detail.assigned_to].name}
                                </span>
                            )}
                        </div>
                        {detail.description && (
                            <p className="text-sm text-zentra-ink p-3 bg-zentra-bg/40 rounded-md border border-zentra-border">
                                {detail.description}
                            </p>
                        )}
                        <div>
                            <label className="zentra-label">Foto evidencia</label>
                            <PhotoUpload
                                value={detail.photo}
                                onChange={(d) => saveDetail({ photo: d })}
                                testId="incident-detail-photo"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Nota de resolución</label>
                            <textarea
                                className="zentra-textarea min-h-[80px]"
                                defaultValue={detail.resolution_note || ""}
                                onBlur={(e) => {
                                    if (e.target.value !== detail.resolution_note) saveDetail({ resolution_note: e.target.value });
                                }}
                                placeholder="Trabajo realizado, material usado, próximos pasos…"
                                data-testid="incident-resolution"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Reasignar</label>
                            <select
                                className="zentra-select"
                                value={detail.assigned_to || ""}
                                onChange={(e) => saveDetail({ assigned_to: e.target.value || null })}
                            >
                                <option value="">Sin asignar</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>
                                        {e.name} · {e.role}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
